import dayjs from "dayjs";
import { Value } from "@/entities/Finance/Finance.model";

/**
 * Parse window ID to get the number of periods to show
 * Examples:
 * - "l12m" -> 12 (last 12 months)
 * - "l4q" -> 4 (last 4 quarters)
 * - "l3y" -> 3 (last 3 years)
 * - "ytd" -> year to date
 * - "all" -> undefined (show all)
 */
export function parseWindowId(
    windowId: string | undefined,
): number | "ytd" | undefined {
    if (!windowId || windowId === "all") return undefined;
    if (windowId === "ytd") return "ytd";

    // Extract number from patterns like "l12m", "l4q", "l3y"
    const match = windowId.match(/^l(\d+)[mqy]$/);
    if (match) {
        return parseInt(match[1], 10);
    }

    return undefined;
}

/**
 * Filter periods based on the selected window
 * @param periods - Array of period strings (sorted chronologically)
 * @param windowId - Window ID from period selector (e.g., "l12m", "ytd", "all")
 * @returns Filtered array of periods
 */
export function filterPeriodsByWindow(
    periods: string[],
    windowId: string | undefined,
): string[] {
    const parsedWindow = parseWindowId(windowId);

    // If no window or "all", return all periods
    if (parsedWindow === undefined) {
        return periods;
    }

    // Handle YTD (Year to Date)
    if (parsedWindow === "ytd") {
        const now = dayjs();
        const startOfYear = now.startOf("year");
        return periods.filter((period) => {
            const periodDate = dayjs(period);
            return (
                periodDate.isValid() &&
                periodDate.isAfter(startOfYear.subtract(1, "day"))
            );
        });
    }

    // Handle "last N periods" - take the most recent N periods
    if (typeof parsedWindow === "number") {
        return periods.slice(-parsedWindow);
    }

    return periods;
}

/**
 * Filter metric values based on the selected window
 * @param values - Array of metric values
 * @param windowId - Window ID from period selector
 * @returns Metric values with filtered periods
 */
export function filterMetricValuesByWindow(
    values: Value[],
    windowId: string | undefined,
): Value[] {
    if (!windowId || windowId === "all") {
        return values;
    }

    // Sort values by period first
    const sortedValues = [...values].sort((a, b) =>
        a.period.localeCompare(b.period),
    );
    const periods = sortedValues.map((v) => v.period);
    const filteredPeriods = new Set(filterPeriodsByWindow(periods, windowId));

    // Return values that match the filtered periods
    return sortedValues.filter((v) => filteredPeriods.has(v.period));
}
